import { CircleQuestionMark } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import PopupCreation from '../../components_v2/popup/PopupCreation';
import { BorderColor, DarkGreySidely2, LightGreySidely2 } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';

// The guides of a promotion field's formula and display format, opened from
// a small "?" beside each input of the field popup. The examples are code
// and stay as they are in every language; the explanations are translated.

export type GuideKind = 'formula' | 'format';

const t = (key: string) => translateToString(`form_editor.guide.${key}`);

export function GuideButton(props: { onClick: () => void }): JSX.Element {
	return <HelpIcon
		role='button'
		title={t('open')}
		onClick={e => { e.preventDefault(); e.stopPropagation(); props.onClick(); }}
	><CircleQuestionMark size={14} /></HelpIcon>;
}

/** An input label followed by its "?". */
export function LabelWithGuide(props: { label: string, onGuide: () => void }): JSX.Element {
	return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{props.label}<GuideButton onClick={props.onGuide} /></span>;
}

type Row = [code: string, description: string];

function Section(props: { title: string, intro?: string, rows?: Row[] }): JSX.Element {
	return <SectionBox>
		<SectionTitle>{props.title}</SectionTitle>
		{props.intro && <Intro>{props.intro}</Intro>}
		{props.rows && <Rows>
			{props.rows.map(([code, description]) => <React.Fragment key={code}>
				<Code>{code}</Code>
				<span>{description}</span>
			</React.Fragment>)}
		</Rows>}
	</SectionBox>;
}

function FormulaGuideContent(): JSX.Element {
	return <>
		<Intro>{t('formula.intro')}</Intro>
		<Section title={t('formula.references')} rows={[
			['[Remise]', t('formula.ref_name')],
			['[remise]', t('formula.ref_slug')],
			['[product.price]', t('formula.ref_product_price')],
			['[product.name]  [product.reference]  [product.barcode]', t('formula.ref_product_text')],
			['[product.stock]  [product.recommended_price]', t('formula.ref_product_numbers')],
			['[product.Prix catalogue]', t('formula.ref_product_custom')],
			['[promotion.name]  [promotion.days]', t('formula.ref_promotion')],
			['[promotion.products]  [promotion.companies]', t('formula.ref_promotion_counts')],
		]} />
		<Section title={t('formula.operators')} rows={[
			['+  -  *  /', t('formula.op_arithmetic')],
			['=  <>  <  <=  >  >=', t('formula.op_compare')],
			['&', t('formula.op_join')],
			['"texte"  12.5  TRUE  BLANK', t('formula.op_literals')],
		]} />
		<Section title={t('formula.functions')} rows={[
			['IF(condition, alors, sinon)', t('formula.fn_if')],
			['IFS(cond1, val1, cond2, val2, …, sinon)', t('formula.fn_ifs')],
			['AND(…)  OR(…)  NOT(x)', t('formula.fn_logic')],
			['ROUND(x, 2)  ABS(x)', t('formula.fn_round')],
			['MIN(a, b, …)  MAX(a, b, …)', t('formula.fn_minmax')],
			['COALESCE(a, b, …)  ISBLANK(x)', t('formula.fn_blank')],
			['TEXT(x, 1)  CONCAT(a, b, …)', t('formula.fn_text')],
		]} />
		<Section title={t('formula.aggregates')} intro={t('formula.aggregates_intro')} rows={[
			['SUM([Quantité] * [product.price])', t('formula.agg_sum')],
			['AVG(x)  MIN(x)  MAX(x)', t('formula.agg_avg')],
			['COUNT([Quantité])', t('formula.agg_count')],
		]} />
		<Section title={t('formula.examples')} rows={[
			['[Budget] * (1 - [Remise] / 100)', t('formula.ex_discount')],
			['[Quantité] * [product.price]', t('formula.ex_line')],
			['ROUND([Coût] / [Budget] * 100, 1)', t('formula.ex_share')],
			['IF([Remise] > 30, "⚠️ exception", "ok")', t('formula.ex_flag')],
		]} />
		<Section title={t('formula.notes')} intro={t('formula.notes_text')} />
	</>;
}

function FormatGuideContent(): JSX.Element {
	return <>
		<Intro>{t('format.intro')}</Intro>
		<Section title={t('format.placeholders')} rows={[
			['{}', t('format.ph_value')],
			['{:2}', t('format.ph_decimals')],
			['{:0}', t('format.ph_round')],
			['€', t('format.ph_none')],
		]} />
		<Section title={t('format.examples')} rows={[
			['{:2} €', '3.14159 → 3.14 €'],
			['{:0} pcs', '2.6 → 3 pcs'],
			['🎯 {}', '7 → 🎯 7'],
			['{:1} %', '12.345 → 12.3 %'],
		]} />
		<Section title={t('formula.notes')} intro={t('format.notes_text')} />
	</>;
}

export function FieldGuidePopup(props: { kind: GuideKind | undefined, onClose: () => void }): JSX.Element {
	return <Popup
		isOpen={props.kind !== undefined}
		popupMode={PopupMode.Centered}
		onClickOut={props.onClose}
		popupStyle={{ width: '640px', height: '80vh', zIndex: 1000 }}
	>
		<PopupCreation
			title={t(props.kind === 'format' ? 'format.title' : 'formula.title')}
			onClose={props.onClose}
			hideValidation
		>
			<Body>{props.kind === 'format' ? <FormatGuideContent /> : <FormulaGuideContent />}</Body>
		</PopupCreation>
	</Popup>;
}

const HelpIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: ${DarkGreySidely2};
	&:hover { opacity: 0.7; }
`;

const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: 18px;
	padding: 4px 4px 16px;
	font-size: 13px;
`;

const SectionBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const SectionTitle = styled.div`
	font-weight: 600;
	font-size: 14px;
`;

const Intro = styled.div`
	color: ${DarkGreySidely2};
	line-height: 1.5;
`;

const Rows = styled.div`
	display: grid;
	grid-template-columns: minmax(180px, max-content) 1fr;
	gap: 6px 14px;
	align-items: baseline;
`;

const Code = styled.code`
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
	background: ${LightGreySidely2};
	border: 1px solid ${BorderColor};
	border-radius: 4px;
	padding: 1px 6px;
	white-space: pre-wrap;
	word-break: break-word;
`;
